/**
 * Catálogo de telas — **dados puros, sem React**.
 *
 * Fonte única de: rótulo, rota, hierarquia e chave de permissão.
 *
 * Por que separado de `layout/nav.ts`: aquele arquivo adiciona os ícones, que são
 * componentes React. Misturar dado com apresentação impedia testar as permissões
 * fora do navegador — o teste quebrava ao tentar importar JSX no Node.
 *
 * Agora:
 *   `lib/telas.ts`     → o que existe (dado)
 *   `layout/nav.ts`    → como aparece na sidebar (ícones)
 *   `lib/permissoes.ts`→ quem pode ver o quê
 *
 * As rotas são exatamente as do sistema atual — não podem mudar, senão quebram
 * links salvos e o hábito da equipe.
 */

export interface ItemTela {
  label: string
  to: string
  /** Cabeçalho de grupo dentro do submenu. */
  group?: string
}

export interface SecaoTela {
  label: string
  to: string
  /** Nome do ícone, resolvido em `layout/nav.ts`. */
  icone: string
  /** Só administrador vê. */
  adminOnly?: boolean
  children?: ItemTela[]
}

export const SECOES: SecaoTela[] = [
  { label: 'Dashboard', to: '/dashboard', icone: 'dashboard' },

  {
    label: 'Importação',
    to: '/importacao',
    icone: 'import',
    children: [
      { label: 'Processos', to: '/importacao' },
      { label: 'Estimativas', to: '/estimativas' },
      { label: 'Catálogo de Produtos', to: '/catalog' },
      { label: 'Nova LPCO', to: '/lpco/new', group: 'LPCO' },
      { label: 'Anexar em LPCO existente', to: '/lpco/anexar' },
      { label: 'Acompanhamento LPCO', to: '/lpco/painel' },
      { label: 'Envio em Lote', to: '/batch' },
      { label: 'Histórico', to: '/history' },
      { label: 'Mapeamento', to: '/mappings' },
      { label: 'DUIMP', to: '/duimp', group: 'DUIMP' },
      { label: 'Retificar DUIMP', to: '/duimp/retificacao' },
    ],
  },

  {
    label: 'Exportação',
    to: '/exportacao',
    icone: 'export',
    children: [{ label: 'Processos', to: '/exportacao' }],
  },

  {
    label: 'Agenciamento',
    to: '/agenciamento',
    icone: 'ship',
    children: [{ label: 'Processos', to: '/agenciamento' }],
  },

  {
    label: 'Credenciamento',
    to: '/credenciamento',
    icone: 'shield',
    children: [
      { label: 'Credenciamentos', to: '/credenciamento' },
      { label: 'Alertas de Vencimento', to: '/credenciamento/alertas' },
    ],
  },

  { label: 'Empresas', to: '/empresas', icone: 'building' },
  { label: 'Financeiro', to: '/financeiro', icone: 'money' },
  { label: 'Emissão de AWB', to: '/awb', icone: 'plane' },
  { label: 'Ferramentas', to: '/ferramentas/pdf', icone: 'settings', children: [
    { label: 'PDF/A e propriedades', to: '/ferramentas/pdf' },
  ] },
  { label: 'Configurações', to: '/settings', icone: 'settings' },
  { label: 'Painel Admin', to: '/admin', icone: 'admin', adminOnly: true },
]

/** Rótulos para o breadcrumb das telas profundas. */
export const ROTULOS: Record<string, string> = {
  dashboard: 'Dashboard',
  importacao: 'Importação',
  exportacao: 'Exportação',
  agenciamento: 'Agenciamento',
  credenciamento: 'Credenciamento',
  empresas: 'Empresas',
  financeiro: 'Financeiro',
  estimativas: 'Estimativas',
  awb: 'Emissão de AWB',
  duimp: 'DUIMP',
  retificacao: 'Retificação',
  rascunhos: 'Rascunhos',
  historico: 'Histórico',
  ia: 'DUIMP com IA',
  catalog: 'Catálogo de Produtos',
  lpco: 'LPCO',
  new: 'Nova',
  anexar: 'Anexar documentos',
  painel: 'Acompanhamento',
  batch: 'Envio em Lote',
  history: 'Histórico',
  mappings: 'Mapeamento',
  settings: 'Configurações',
  admin: 'Painel Admin',
  alertas: 'Alertas de Vencimento',
  novo: 'Novo',
  nova: 'Nova',
  editar: 'Editar',
}
